import type { HTMLAttributes } from "react";
import { cn } from "../../../utils/cn";

export interface PaginationProps extends HTMLAttributes<HTMLDivElement> {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}

export function Pagination({
  page,
  pageSize,
  total,
  onChange,
  className,
  ...props
}: PaginationProps) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const clamped = Math.min(Math.max(1, page), pages);
  const go = (p: number) => onChange(Math.min(Math.max(1, p), pages));
  const items: number[] = [];
  const maxShown = 7;
  if (pages <= maxShown) {
    for (let i = 1; i <= pages; i++) items.push(i);
  } else {
    const left = Math.max(1, clamped - 2);
    const right = Math.min(pages, clamped + 2);
    items.push(1);
    if (left > 2) items.push(-1);
    for (let i = left; i <= right; i++) items.push(i);
    if (right < pages - 1) items.push(-2);
    items.push(pages);
  }
  return (
    <div className={cn("mt-4 flex items-center justify-center gap-1", className)} {...props}>
      <button
        className="rounded-sm border border-muted bg-surface px-2 py-1 text-sm text-text disabled:opacity-40"
        onClick={() => go(clamped - 1)}
        disabled={clamped <= 1}
        aria-label="Page précédente"
      >
        Précédent
      </button>
      {items.map((i, idx) =>
        i < 0 ? (
          <span key={`ellipsis-${idx}`} className="px-2 text-muted">
            …
          </span>
        ) : (
          <button
            key={i}
            className={cn(
              "rounded-sm border px-2 py-1 text-sm",
              i === clamped
                ? "border-accent bg-accent text-bg"
                : "border-muted bg-surface text-text hover:bg-text/5",
            )}
            onClick={() => go(i)}
            aria-current={i === clamped ? "page" : undefined}
          >
            {i}
          </button>
        ),
      )}
      <button
        className="rounded-sm border border-muted bg-surface px-2 py-1 text-sm text-text disabled:opacity-40"
        onClick={() => go(clamped + 1)}
        disabled={clamped >= pages}
        aria-label="Page suivante"
      >
        Suivant
      </button>
    </div>
  );
}

export default Pagination;
